// Regalo Quick-Step: con más de 40 m² de laminado Quick-Step en el carrito, el rodapié lacado blanco de regalo
// (piezas = floor(m² × 1,1 / 2,25)) se añade, se ajusta y se quita solo, salvo que el cliente pulse «No lo necesito»
// (atributo del carrito _mp_regalo_qs_rechazado = '1'; «Añadirlo de nuevo» lo borra). El cálculo lo hace Liquid
// (snippets/mp-regalo-qs-state.liquid); este script solo compara «lo que debería haber» con «lo que hay».
//
// Sin bucles ni carreras:
// - Nunca escribe si el carrito ya está como debe (se compara antes de llamar a /cart/*.js).
// - Una sola pasada a la vez (running) y con espera de 350 ms tras el último cambio (debounce).
// - Como mucho una corrección por estado del carrito: si una corrección no deja el carrito como debe
//   (sin stock, producto sin publicar…), ese mismo estado no se vuelve a corregir. Tope de 12 por página.
// - Su propio evento cart:change (baseEvent «mp-regalo-qs») solo actualiza la vista, nunca corrige.
// - Es solo una ayuda en el navegador: la API del carrito se puede llamar a mano. Los pedidos se
//   comprueban después en la app (fuera de este paquete).
(() => {
  if (window.MPRegaloQS) return;
  const SECTION = 'mp-regalo-qs-data';
  const PROP = '_mp_regalo_qs';
  const PROP_VALUE = 'quick-step-mas-de-40m2';
  const PREF_KEY = 'mp_regalo_qs_altura';
  const DECLINED = '_mp_regalo_qs_rechazado';
  const OWN_EVENT = 'mp-regalo-qs';
  const MAX_CORRECTIONS = 12;
  const root = () => window.Shopify?.routes?.root || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const nf = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 });
  const m2 = u => nf.format(u / 10000);

  let latest = null, timer = null, queued = null, running = false, rerun = false, corrections = 0, status = '';
  const tried = new Set();

  const readPref = () => { try { const v = sessionStorage.getItem(PREF_KEY); return v === '8' || v === '10' ? v : null; } catch (e) { return null; } };
  const writePref = v => { try { sessionStorage.setItem(PREF_KEY, v); } catch (e) {} };

  const parse = text => { try { const s = JSON.parse(text); return s && s.v === 1 ? s : null; } catch (e) { return null; } };
  const stateFromHtml = html => {
    if (typeof html !== 'string') return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-mp-regalo-qs-state]');
    return el ? parse(el.textContent) : null;
  };
  const stateFromDom = () => {
    const all = document.querySelectorAll('script[data-mp-regalo-qs-state]');
    return all.length ? parse(all[all.length - 1].textContent) : null;
  };
  const fetchState = async () => {
    const response = await fetch(`${root()}?sections=${SECTION}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('sections');
    return stateFromHtml((await response.json())[SECTION]);
  };

  // Lo que debería haber en el carrito con este estado.
  const desired = s => {
    if (!s || !s.config) return null;
    if (!s.eligible || s.declined) return { variant: null, quantity: 0, height: null };
    const order = [readPref() || s.height || '8'];
    order.push(order[0] === '8' ? '10' : '8');
    const height = order.find(h => s.variants[h] && s.available[h]);
    if (!height) return { variant: null, quantity: 0, height: null, soldOut: true };
    return { variant: s.variants[height], quantity: s.pieces, height };
  };

  // Diferencia entre lo que hay y lo que debería haber; vacío si no hay nada que hacer.
  const plan = (s, d) => {
    const updates = {};
    let keep = null;
    if (d.variant) keep = s.gift.find(line => line.variant_id === d.variant && line.tagged) || null;
    if (d.soldOut) return { updates, add: null, keep };
    s.gift.forEach(line => { if (line !== keep) updates[line.key] = 0; });
    if (keep && keep.quantity !== d.quantity) updates[keep.key] = d.quantity;
    const add = d.variant && !keep ? { id: d.variant, quantity: d.quantity, properties: { [PROP]: PROP_VALUE } } : null;
    return { updates, add, keep };
  };
  const isEmpty = p => !p.add && Object.keys(p.updates).length === 0;
  const signature = (s, d) => JSON.stringify([s.m2u, s.b2b, s.eligible, s.declined, s.pieces, d.variant,
    s.gift.map(l => [l.key, l.variant_id, l.quantity, l.tagged])]);

  const sectionIds = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const page = document.querySelector('.cart-page')?.closest('.shopify-section');
    if (page) sections.push(page.id.replace('shopify-section-', ''));
    sections.push(SECTION);
    return [...new Set(sections)].slice(-5);
  };

  // La página del carrito: se sustituye la sección con el HTML recibido (el tema, en cambio, recarga).
  const refreshPage = cart => {
    const page = document.querySelector('.cart-page')?.closest('.shopify-section');
    if (!page) return;
    const html = cart.sections?.[page.id.replace('shopify-section-', '')];
    const replacement = html && new DOMParser().parseFromString(html, 'text/html').getElementById(page.id);
    if (replacement) { page.replaceChildren(...replacement.childNodes); return; }
    try {
      if (sessionStorage.getItem('mp_regalo_qs_reload') === String(cart.token || '')) return;
      sessionStorage.setItem('mp_regalo_qs_reload', String(cart.token || ''));
    } catch (e) { return; }
    window.location.reload();
  };

  const publish = cart => {
    refreshPage(cart);
    const sections = cart.sections || {};
    const complete = Object.keys(sections).length > 0 && Object.values(sections).every(html => typeof html === 'string');
    document.documentElement.dispatchEvent(complete
      ? new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: OWN_EVENT, cart } })
      : new CustomEvent('cart:refresh', { bubbles: true }));
  };

  const notices = () => document.querySelectorAll('[data-mp-regalo-qs-notice]');
  const setBusy = busy => notices().forEach(n => { if (busy) n.setAttribute('aria-busy', 'true'); else n.removeAttribute('aria-busy'); });
  const setStatus = text => {
    status = text || '';
    document.querySelectorAll('[data-mp-regalo-status]').forEach(el => { el.textContent = status; });
  };

  const apply = async (s, d, p) => {
    running = true; setBusy(true);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      if (p.add) {
        // Primero se añade el nuevo; solo si entra, se quitan los anteriores (al cambiar 8 ↔ 10 cm nunca se
        // queda el carrito sin regalo por un fallo a medias).
        const added = await fetch(`${root()}cart/add.js`, { method: 'POST', headers, body: JSON.stringify({ items: [p.add] }) });
        if (!added.ok) throw new Error('add');
      }
      const response = await fetch(`${root()}cart/update.js`, { method: 'POST', headers,
        body: JSON.stringify({ updates: p.updates, sections: sectionIds().join(',') }) });
      if (!response.ok) throw new Error('update');
      const cart = await response.json();
      const after = stateFromHtml(cart.sections?.[SECTION]);
      latest = after || latest;
      const left = after && desired(after) ? plan(after, desired(after)) : null;
      setStatus(left && !isEmpty(left) ? failText(after) : '');
      publish(cart);
    } catch (error) {
      setStatus(failText(s));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      running = false; setBusy(false);
      if (rerun) { rerun = false; schedule(null); }
    }
  };

  const failText = s => s && s.eligible
    ? 'No hemos podido ajustar el rodapié de regalo en el carrito. Recarga la página; si sigue igual, escríbenos y lo revisamos.'
    : '';

  const reconcile = async (s, { userAction = false } = {}) => {
    if (!s) return;
    latest = s;
    updatePdp(s);
    const d = desired(s);
    if (!d) return;
    if (d.soldOut) { setStatus('Ahora mismo no podemos añadir el rodapié de regalo. Escríbenos y lo revisamos.'); return; }
    const p = plan(s, d);
    if (isEmpty(p)) { setStatus(''); return; }
    const sig = signature(s, d);
    if (!userAction && (tried.has(sig) || corrections >= MAX_CORRECTIONS)) { setStatus(failText(s)); return; }
    tried.add(sig); corrections++;
    await apply(s, d, p);
  };

  const run = async () => {
    timer = null;
    if (running) { rerun = true; return; }
    let s = queued; queued = null;
    try { if (!s) s = await fetchState(); } catch (e) { return; }
    await reconcile(s);
  };
  const schedule = (s, delay = 350) => {
    if (s) queued = s;
    clearTimeout(timer);
    timer = setTimeout(run, delay);
  };

  document.addEventListener('cart:prepare-bundled-sections', event => {
    const list = event.detail?.sections;
    if (Array.isArray(list) && !list.includes(SECTION) && list.length < 5) list.push(SECTION);
  });

  document.addEventListener('cart:change', event => {
    const cart = event.detail?.cart;
    const s = stateFromHtml(cart?.sections?.[SECTION]);
    if (event.detail?.baseEvent === OWN_EVENT) { if (s) { latest = s; updatePdp(s); } return; }
    schedule(s);
  });
  document.addEventListener('cart:refresh', () => schedule(null));
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(null); });
  // El cajón se repinta después de cart:change: se vuelve a poner el mensaje de estado y, por si la sección de
  // datos no llegara (null), se usa el estado que trae el propio cajón recién pintado.
  document.addEventListener('cart-drawer:refreshed', () => {
    setStatus(status);
    const s = stateFromDom();
    if (s && !running) schedule(s);
  });

  // Altura 8 / 10 cm: lo elige el cliente; se recuerda en la sesión del navegador.
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-mp-regalo-altura]');
    if (!button) return;
    event.preventDefault();
    const height = button.getAttribute('data-mp-regalo-altura');
    if (button.getAttribute('aria-pressed') === 'true' || running) return;
    writePref(height);
    clearTimeout(timer); timer = null;
    let s;
    try { s = await fetchState(); } catch (e) { setStatus(failText(latest)); return; }
    await reconcile(s, { userAction: true });
  });

  // «No lo necesito» / «Añadirlo de nuevo»: se guarda en el carrito (sirve en cualquier pestaña y llega al pedido)
  // y después se quita o se vuelve a poner la línea de regalo.
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-mp-regalo-rechazar]');
    if (!button) return;
    event.preventDefault();
    if (running) return;
    const declined = button.getAttribute('data-mp-regalo-rechazar') === '1';
    clearTimeout(timer); timer = null;
    running = true; setBusy(true);
    let cart = null;
    try {
      const response = await fetch(`${root()}cart/update.js`, { method: 'POST', headers,
        body: JSON.stringify({ attributes: { [DECLINED]: declined ? '1' : '' }, sections: sectionIds().join(',') }) });
      if (!response.ok) throw new Error('attributes');
      cart = await response.json();
    } catch (e) {
      setStatus('No hemos podido guardar tu elección. Recarga la página e inténtalo de nuevo.');
    } finally {
      running = false; setBusy(false);
    }
    if (!cart) return;
    let s = stateFromHtml(cart.sections?.[SECTION]);
    publish(cart);
    try { if (!s) s = await fetchState(); } catch (e) { return; }
    await reconcile(s, { userAction: true });
  });

  // Si el cliente va a pagar con una corrección pendiente, se hace ya (mp-cart-guards espera a que acabe).
  document.addEventListener('pointerdown', event => {
    if (timer && event.target.closest('[name="checkout"]')) { clearTimeout(timer); run(); }
  }, true);

  // ── Ficha de producto: «Con 60 m², 29 piezas de rodapié de regalo» (sigue a la calculadora, evento mp:calc).
  let calcU = 0;
  const updatePdp = s => {
    document.querySelectorAll('[data-mp-regalo-pdp]').forEach(block => {
      if (s) {
        const own = Number(block.dataset.productId);
        const other = (s.qs || []).filter(l => l.product_id !== own).reduce((sum, l) => sum + l.m2u, 0);
        block.dataset.otherU = String(other);
      }
      const line = block.querySelector('[data-mp-regalo-pdp-line]');
      if (!line) return;
      const other = Number(block.dataset.otherU) || 0;
      const threshold = Number(block.dataset.threshold) || 400000;
      const piece = Number(block.dataset.pieceU) || 22500;
      if (calcU <= 0) { line.hidden = true; line.textContent = ''; return; }
      const total = calcU + other;
      const pieces = Math.floor(total * 11 / (piece * 10));
      const plus = other > 0 ? ' (sumando el laminado Quick-Step de tu carrito)' : '';
      let text;
      if (total > threshold && pieces > 0) {
        text = `Con ${m2(total)} m²${plus}, ${pieces} ${pieces === 1 ? 'pieza' : 'piezas'} de rodapié de regalo (${pieces} × ${m2(piece)} m).`;
      } else if (total === threshold) {
        text = `Con 40 m² justos aún no: con una caja más, el rodapié va de regalo.`;
      } else {
        text = `Con ${m2(total)} m²${plus}, te faltan ${m2(Math.ceil((threshold - total) / 100) * 100)} m² para llevarte el rodapié de regalo.`;
      }
      line.textContent = text;
      line.hidden = false;
    });
  };
  document.addEventListener('mp:calc', event => {
    const value = Number(event.detail?.m2);
    calcU = Number.isFinite(value) && value > 0 ? Math.round(value * 10000) : 0;
    updatePdp(null);
  });

  const init = () => {
    const s = stateFromDom();
    if (s) schedule(s, 0);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.MPRegaloQS = { desired, plan, signature, reconcile: () => schedule(null, 0) };
})();
